<template>
  <div>
    <page-header
      :actions="headerActionConfig"
      left-side-class="col-xl-4"
      :page="pageHeaderConfig"
      right-side-class="col-xl-8 align-items-end d-flex pb-2 justify-content-end"
    />
    <filter-manager
      ref="trainingHistoryFilterRef"
      v-model="filter"
      :filters="filterManagerConfig"
      @academicYear="onChangeAcademicYear"
      @activity="onChangeActivity"
      @center="onChangeCenter"
      @month="onChangeMonth"
      @provider="onChangeProvider"
      @search="onSearchHandler"
      @serviceLocation="onChangeLocationService"
      @specialist="onChangeSpecialist"
    />
    <custom-separator
      class="pt-3 my-4"
      text="Resultado de Adiestramiento, Taller y COMPU"
    />
    <div
      v-for="(item, index) in trainingResult"
      :key="'training-history' + index"
    >
      <custom-callout class="mb-3 custom-callout" :has-toggle="false">
        <div class="row">
          <div class="col-md-2 border-right">
            <div class="font-weight-bold color-black font-16">
              {{ item.activity }}
            </div>
            <div class="font-weight-normal color-secondary-6 font-14">
              {{ item.center }}
            </div>
          </div>
          <div class="col-md-8">
            <div class="row">
              <div class="col">
                <div class="color-secondary-6 font-14">
                  Fecha:
                </div>
                <div class="font-weight-normal color-black font-16">
                  {{ item.date }}
                </div>
              </div>
              <div class="col">
                <div class="color-secondary-6 font-14">
                  Estudiante / SIE:
                </div>
                <div class="font-weight-normal color-black font-16">
                  {{ item.student.fullName }}
                </div>
                <div class="font-weight-normal color-secondary-6 font-14">
                  {{
                    item.student.studentId == 0 ? '-' : item.student.studentId
                  }}
                </div>
              </div>
              <div class="col">
                <div class="color-secondary-6 font-14">
                  Horario:
                </div>
                <div class="font-weight-normal color-black font-16">
                  {{ item.timeEnd.totalMinutes == 0 ? '-' : item.schedule }}
                </div>
              </div>
              <div class="col">
                <div class="color-secondary-6 font-14">
                  Especialista:
                </div>
                <div class="font-weight-normal color-black font-16">
                  {{ item.specialist }}
                </div>
              </div>
            </div>
          </div>
          <div class="col-2 text-center">
            <drop-down
              class="actions-dropdown"
              :data-item="{ item, index }"
              :items="actionItems"
            />
          </div>
        </div>
      </custom-callout>
    </div>
    <result-not-found v-if="trainingResult.length == 0" />
    <pagination
      v-if="
        trainingResult.length > 0 &&
          pagination.totalItemCount > paginationConfig.initialPageSize
      "
      :data="pagination"
      :show-text="true"
      @pagination-go-page="goToPageHandler"
    />
  </div>
</template>

<script>
import { createNamespacedHelpers, mapMutations } from 'vuex';
import PageHeader from 'PageHeader';
import FilterManager from 'FilterManager';
import CustomSeparator from 'custom-separator';
import CustomCallout from 'custom-callout';
import Pagination from 'pagination';
import ResultNotFound from 'custom-result-not-found';
import roles from '@/utils/constants/roles.js';
import { name as moduleName } from '@/modules/trainingHistory/store';
import {
  ACTION_TYPES,
  MUTATION_TYPES,
} from '@/modules/trainingHistory/store/types';
const { mapActions, mapState, mapGetters } = createNamespacedHelpers(
  moduleName
);
const TRAINING_DETAILS_ROUTE_NAME = 'TrainingDetails';
import { _deleteTraining } from '@/modules/trainingHistory/api';
import alert from 'utils/mixins/alert.mixin';
import billingStatus from '@/utils/constants/billingStatus.js';

export default {
  name: 'TrainingHistory',
  components: {
    PageHeader,
    FilterManager,
    CustomSeparator,
    CustomCallout,
    Pagination,
    ResultNotFound,
  },
  mixins: [alert],
  computed: {
    uniqueProviders() {
      const seen = new Set();
      return this.providers.filter((provider) => {
        if (!seen.has(provider?.id)) {
          seen.add(provider?.id);
          return true;
        }
        return false;
      });
    },
    ...mapGetters({
      companyId: 'getCompanyId',
      providers: 'getCurrentCompanies',
      currentCompany: 'getCurrentCompany',
    }),
    ...mapState({
      filterValue: (state) => state.filter,
      academicYears: (state) => state.catalog.academicYears,
      months: (state) => state.catalog.months,
      specialists: (state) => state.catalog.specialists,
      serviceLocations: (state) => state.catalog.serviceLocations,
      activities: (state) => state.catalog.activities,
      centers: (state) => state.catalog.centers,
      providersAdmin: (state) => state.catalog.providers,
      trainingResult: (state) => state.trainingSearchResults.data,
      pagination: (state) => state.trainingSearchResults.pagination,
      paginationConfig: (state) => state.trainingSearchResults.paginationConfig,
      finilazedBillingStatus: (state) => state.finilazedBillingStatus,
    }),
    filter: {
      get() {
        return this.filterValue;
      },
      set(value) {
        this[MUTATION_TYPES.SET_FILTER](value);
      },
    },
    pageHeaderConfig() {
      return {
        title: 'Adiestramiento, Taller y COMPU: Historial',
        icon: 'fas fa-user-tie',
        routes: [
          'Proveedor',
          'Adiestramiento, Taller y COMPU',
          'Adiestramiento, Taller y COMPU: Historial',
        ],
      };
    },
    headerActionConfig() {
      return [
        {
          text: 'Añadir',
          icon: 'fas fa-file-plus',
          variant: 'success',
          click: this.addHandler,
          visible: this.userHasPermissions('provider.training.add'),
        },
      ];
    },
    filterManagerConfig() {
      return [
        {
          name: 'academicYear',
          options: this.academicYears,
          initialValue: this.currentAcademicYear(),
          component: 'CustomDropDown',
          fieldKey: 'id',
          fieldName: 'name',
          class: 'col-md-3',
          clearModelsOnUpdate: ['month', 'specialist', 'center'],
          onChanged: this.onChangeAcademicYear,
          immediateOnChanged: true,
          clearable: false,
        },
        {
          name: 'month',
          options: this.months,
          initialValue: this.filter.month,
          component: 'CustomDropDown',
          placeholder: 'Seleccione Mes',
          fieldKey: 'monthNumber',
          fieldName: 'monthDesc',
          class: 'col-md-3',
        },
        {
          name: 'activity',
          label: 'Actividad:',
          options: this.activities,
          initialValue: this.filter.activity,
          component: 'CustomDropDown',
          placeholder: 'Seleccione',
          fieldKey: 'id',
          fieldName: 'name',
          class: 'col-md-4',
          isAdvanced: true,
        },
        {
          name: 'center',
          label: 'Centro:',
          options: this.centers,
          initialValue: this.filter.center,
          component: 'CustomDropDown',
          placeholder: 'Seleccione',
          fieldKey: 'id',
          fieldName: 'name',
          class: 'col-md-4',
          isAdvanced: true,
        },
        {
          name: 'serviceLocation',
          label: 'Lugar de Servicio:',
          options: this.serviceLocations,
          initialValue: this.filter.serviceLocation,
          component: 'CustomDropDown',
          placeholder: 'Seleccione',
          fieldKey: 'id',
          fieldName: 'name',
          class: 'col-md-4',
          isAdvanced: true,
          onChanged: this.onChangeLocationService,
          visible: this.isServicePlacesVisible,
          clearModelsOnUpdate: ['specialist'],
          validations: 'required',
          disabled: this.serviceLocations.length == 1,
        },
        {
          label: this.enableAllProviders ? 'Especialista:' : '',
          name: 'specialist',
          options: this.specialists,
          initialValue: this.filter.specialist,
          component: 'CustomDropDown',
          placeholder: 'Seleccione Especialista',
          fieldKey: 'id',
          fieldName: 'name',
          class: this.enableAllProviders ? 'col-md-4' : 'col-md-6',
          isAdvanced: this.enableAllProviders,
          disabled: this.specialists.length == 1,
        },
        {
          name: 'provider',
          label: !this.enableAllProviders ? 'Proveedor:' : '',
          options: this.enableAllProviders
            ? this.providersAdmin
            : this.uniqueProviders,
          initialValue: this.enableAllProviders
            ? this.filter.provider
            : this.currentCompany,
          clearable: this.enableAllProviders ? true : false,
          component: 'CustomDropDown',
          placeholder: 'Seleccione',
          fieldKey: 'id',
          fieldName: 'name',
          class: !this.enableAllProviders ? 'col-md-4' : 'col-md-6',
          isAdvanced: !this.enableAllProviders,
          disabled: this.isProvider,
          onChanged: this.onChangeProvider,
          clearModelsOnUpdate: ['specialist', 'serviceLocation'],
        },
      ];
    },
    enableAllProviders() {
      return [
        roles.Administrador,
        roles.InterventorCentro,
        roles.DirectorCentro,
        roles.ProviderCPA,
        roles.AsistenciaTecnica,
        roles.CentroIsla,
        roles.AdminProvider,
        roles.UMESRAdmin,
      ].includes(this.userRole);
    },
    actionItems() {
      return [
        {
          name: 'goto',
          label: 'Entrar',
          icon: 'fas fa-file-import color-primary-1 pr-1',
          click: ({ item }) => this.goToDetail(item),
          border: 'bottom',
        },
        {
          name: 'edit',
          label: 'Editar',
          icon: 'fas fa-edit color-primary-1 pr-1',
          click: ({ item }) => this.goToEditDetail(item),
        },
        {
          name: 'delete',
          label: 'Eliminar',
          icon: 'fas fa-times-circle color-primary-4 pr-1',
          click: ({ item }) => this.onDeleteHandler(item),
        },
      ];
    },
    isProvider() {
      return this.uniqueProviders.length == 1;
    },
    currentCompanyId() {
      return this.companyId?.indexOf(',') > -1
        ? this.companyId.split(',')[0]
        : this.companyId;
    },
    isServicePlacesVisible() {
      return this.userHasPermissions('level.location');
    },
  },
  mounted() {
    this[ACTION_TYPES.GET_ACADEMIC_YEAR]();
    this[ACTION_TYPES.GET_ACTIVITY]();
    this.loadServiceLocation();
  },
  methods: {
    async loadServiceLocation() {
      if (this.isServicePlacesVisible) {
        await this[ACTION_TYPES.GET_SERVICE_LOCATIONS]({
          companyId: this.currentCompany?.id,
        });
        if (this.serviceLocations?.length === 1) {
          this.filter.serviceLocation = this.serviceLocations[0];
          await this[ACTION_TYPES.GET_SPECIALIST]({
            academicYearId: this.filter.academicYear?.id,
            companyId: this.currentCompany?.id,
            serviceLocationId: this.serviceLocations[0]?.id,
          });
        }
      }
    },
    ...mapActions([
      ACTION_TYPES.GET_ACADEMIC_YEAR,
      ACTION_TYPES.GET_MONTH,
      ACTION_TYPES.GET_SPECIALIST,
      ACTION_TYPES.GET_ACTIVITY,
      ACTION_TYPES.GET_CENTER,
      ACTION_TYPES.GET_PROVIDER,
      ACTION_TYPES.GET_SEARCH_CONSULTING,
      ACTION_TYPES.GET_FINALIZED_BILLING_STATUS,
      ACTION_TYPES.GET_SERVICE_LOCATIONS,
    ]),
    ...mapMutations([MUTATION_TYPES.SET_FILTER]),
    async onChangeAcademicYear(value) {
      this.months = [];
      this.filter.academicYear = value;
      this.filter.month = null;
      this.filter.center = null;

      if (!value?.id) return;

      await this[ACTION_TYPES.GET_MONTH]({
        academicYearId: value?.id,
      });

      if (!this.isServicePlacesVisible) {
        await this[ACTION_TYPES.GET_SPECIALIST]({
          academicYearId: value?.id,
          companyId: this.currentCompanyId,
        });
      }

      await this[ACTION_TYPES.GET_CENTER](value?.id);
      await this[ACTION_TYPES.GET_PROVIDER](value?.id);
    },
    onChangeMonth(value) {
      this.filter.month = value;
    },
    onChangeSpecialist(value) {
      this.filter.specialist = value;
    },
    onChangeActivity(value) {
      this.filter.activity = value;
    },
    onChangeCenter(value) {
      this.filter.center = value;
    },
    async onChangeProvider(value) {
      this.filter.provider = value;

      this.filter.specialist = null;
      this.filter.serviceLocation = null;
      this.specialists = [];
      this.serviceLocations = [];

      await this[ACTION_TYPES.GET_SERVICE_LOCATIONS]({
        companyId: value?.id,
      });

      if (this.serviceLocations.length == 1) {
        this.filter.serviceLocation = this.serviceLocations[0];
        await this[ACTION_TYPES.GET_SPECIALIST]({
          academicYearId: this.filter.academicYear?.id,
          companyId: this.filter.provider?.id,
          serviceLocationId: this.filter.serviceLocation?.id,
        });
      }
    },
    async onChangeLocationService(value) {
      this.filter.serviceLocation = value;

      // Call Specialists
      this.filter.specialist = null;
      this.specialists = [];

      await this[ACTION_TYPES.GET_SPECIALIST]({
        academicYearId: this.filter.academicYear?.id,
        companyId: this.filter.provider?.id,
        serviceLocationId: value?.id,
      });

      if (this.specialists.length == 1) {
        this.filter.specialist = this.specialists[0];
      }
    },
    async callSearchConsulting(
      pageIndex = this.paginationConfig.initialCurrentPage
    ) {
      await this[ACTION_TYPES.GET_SEARCH_CONSULTING]({
        academicYearId: this.filter.academicYear?.id,
        companyId: this.filter.provider?.id
          ? this.filter.provider.id
          : this.currentCompanyId,
        monthNumber: this.filter.month?.monthNumber,
        specialistId: this.filter.specialist?.id,
        activityId: this.filter.activity?.id,
        centerId: this.filter.center?.id,
        serviceLocationId: this.filter.serviceLocation?.id,
        currentPage: pageIndex,
        pageSize: this.paginationConfig.initialPageSize,
      });
    },
    addHandler() {
      this.$router.push({
        name: TRAINING_DETAILS_ROUTE_NAME,
        params: { id: 0 },
      });
    },
    async onSearchHandler() {
      var validate = await this.$refs['trainingHistoryFilterRef'].$refs[
        'formValidator'
      ].validate();
      if (!validate) {
        return;
      }
      this.callSearchConsulting();
    },
    goToPageHandler(page) {
      this.callSearchConsulting(page);
    },
    goToDetail(dataItemIndex) {
      this.$router.push({
        name: TRAINING_DETAILS_ROUTE_NAME,
        params: { id: dataItemIndex?.id },
      });
    },
    async onDeleteHandler(item) {
      if (item?.isBillingSubmitted === true) {
        this.ShowToast(
          '',
          'No se puede eliminar ya que cuenta con una factura en estatus Sometida.',
          'error'
        );
        return;
      }
      if (!(await this.validateBilling(item))) {
        return;
      } else {
        const answer = await this.ShowCancelModal(
          'Confirmación',
          '¿Está seguro que desea eliminar la actividad?',
          'Sí',
          'No'
        );
        if (answer) {
          await _deleteTraining(item.id)
            .then(() => {
              this.ShowToast(
                'Proceso exitoso',
                'La actividad ha sido eliminada exitosamente.',
                'success'
              );
              this.callSearchConsulting();
            })
            .catch(() =>
              this.ShowToast(
                '',
                'No ha sido posible eliminar la actividad.',
                'error'
              )
            );
        }
      }
    },
    async goToEditDetail(item) {
      if (item?.isBillingSubmitted === true) {
        this.ShowToast(
          '',
          'No se puede editar ya que cuenta con una factura en estatus Sometida.',
          'error'
        );
        return;
      }
      if (!(await this.validateBilling(item))) return;

      this.$router.push({
        name: TRAINING_DETAILS_ROUTE_NAME,
        params: { id: item?.id, isEditMode: true },
      });
    },
    async validateBilling(item) {
      await this[ACTION_TYPES.GET_FINALIZED_BILLING_STATUS]({
        academicYearId: this.filter.academicYear?.id,
        companyId: item.companyId,
        monthNumber: item.monthNumber,
        specialistId: item.specialistId,
        centerId: item.centerId,
      });

      if (
        this.finilazedBillingStatus.billingStatusId &&
        this.finilazedBillingStatus.billingStatusId != billingStatus.Preliminar
      ) {
        this.ShowToast(
          '',
          'No es posible editar o eliminar la actividad, ya que se encuentra en una factura sometida.',
          'error'
        );
        return false;
      }

      if (this.finilazedBillingStatus?.finalizedFlag) {
        this.ShowToast(
          '',
          'El periodo del especialista se encuentra finalizado. Para poder editar o eliminar la actividad debe reiniciar el periodo.',
          'error'
        );
        return false;
      }

      return true;
    },
  },
};
</script>
<style scoped lang="scss">
@import '~@/shared/scss/config/fonts.scss';
@import '~@/shared/scss/config/colors.scss';
@media only screen and (max-width: 768px) {
  .row-container {
    div,
    span {
      font-size: map-get($fontsSize, 'font-12') !important;
    }
    .status {
      border-right: unset !important;
      border-bottom: 1px solid map-get($colors, 'secondary-12');
      padding-bottom: map-get($fontsSize, 'font-10');
    }
  }

  ::v-deep .reason {
    em {
      font-size: map-get($fontsSize, 'font-10') !important;
    }
    .text-left {
      font-size: map-get($fontsSize, 'font-12') !important;
    }
  }

  .actions-dropdown {
    ::v-deep {
      .dropdown-toggle {
        width: 100%;
        font-size: 12px;
      }
    }
  }
}
</style>
